/**
 * Small labels: StatusPill (a status word in a tinted pill), MetaChip (grey fact chip), KindChip (CQA/CPP/KPI/IPC),
 * Chip (outlined code chip, e.g. a chart type), StatusText (icon + text, with an optional tooltip) and AlertBox.
 * The caller passes the server's word; the tone is a display mapping only (tokens.js).
 */
import React from 'react';
import { Tooltip } from 'antd';
import { CircleAlert, CircleCheck, CircleMinus, CircleX, Info, TriangleAlert } from 'lucide-react';
import { KIND_TONE } from './tokens';

export const TONE_ICON = { error: CircleX, warning: CircleAlert, ok: CircleCheck, info: Info, neutral: CircleMinus, kpi: Info, teal: Info, accent: Info, caution: TriangleAlert };

const toneClass = (tone) => (tone ? ` cu-tone--${tone}` : '');

export function StatusPill({ tone = 'neutral', children, soft = false, icon = false, testId }) {
  const Icon = TONE_ICON[tone];
  return (
    <span className={`cu-pill${soft ? ' cu-pill--soft' : ''}${toneClass(tone)}`} data-testid={testId}>
      {icon && Icon ? <Icon size={14} aria-hidden /> : null}
      {children}
    </span>
  );
}

export function MetaChip({ children, tooltip, testId }) {
  const chip = <span className="cu-meta" data-testid={testId} tabIndex={tooltip ? 0 : undefined}>{children}</span>;
  return tooltip ? <Tooltip title={tooltip}>{chip}</Tooltip> : chip;
}

export function Chip({ children, tone, title }) {
  return <span className={`cu-chip${toneClass(tone)}`} title={title}>{children}</span>;
}

export function KindChip({ kind }) {
  if (!kind) return null;
  return <Chip tone={KIND_TONE[kind] || 'neutral'}>{kind}</Chip>;
}

/** Icon plus text. `tooltip` (e.g. the rule that fired) makes it focusable. */
export function StatusText({ tone = 'neutral', label, tooltip, muted = false, testId }) {
  const Icon = TONE_ICON[tone] || CircleMinus;
  const body = (
    <span className={`cu-status${muted ? ' cu-status--muted' : ''}${toneClass(tone)}`} data-testid={testId} tabIndex={tooltip ? 0 : undefined}>
      <Icon size={16} data-cu-icon aria-hidden />
      <span>{label}</span>
    </span>
  );
  return tooltip ? <Tooltip title={tooltip}>{body}</Tooltip> : body;
}

/** A tinted notice: icon, optional title, body. */
export function AlertBox({ tone = 'error', title, children, testId }) {
  const Icon = tone === 'warning' ? TriangleAlert : TONE_ICON[tone] || Info;
  return (
    <div className={`cu-alert${toneClass(tone)}`} role="note" data-testid={testId}>
      <Icon size={18} data-cu-icon aria-hidden />
      <div>
        {title ? <div className="cu-alert-title">{title}</div> : null}
        {children}
      </div>
    </div>
  );
}
